import React, { useState, useEffect, useRef } from "react";
import { Globe, ChevronDown, Menu, X } from "lucide-react";

const Navbar = ({ onLanguageChange }) => {
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("es");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const languageMenuRef = useRef(null);

  const toggleLanguageMenu = () => setShowLanguageMenu(!showLanguageMenu);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const changeLanguage = (lang) => {
    setCurrentLanguage(lang);
    onLanguageChange(lang);
    setShowLanguageMenu(false);
  };

  // Cerrar menú de idioma al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Actualizar idioma del documento
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Detectar sección activa en el scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['proyectos', 'formacion', 'sobre-mi', 'contacto'];
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll al hacer clic en enlaces
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  // Traducciones
  const translations = {
    es: {
      projects: "Proyectos",
      education: "Formación",
      aboutMe: "Sobre mí",
      contact: "Contacto",
      language: "Español",
      menu: "Menú",
    },
    en: {
      projects: "Projects",
      education: "Education",
      aboutMe: "About Me",
      contact: "Contact",
      language: "English",
      menu: "Menu",
    },
  };

  const navItems = [
    { key: 'projects', href: 'proyectos' },
    { key: 'education', href: 'formacion' },
    { key: 'aboutMe', href: 'sobre-mi' },
    { key: 'contact', href: 'contacto' },
  ];

  return (
    <>
      {/* Navbar Desktop */}
      <nav className="fixed top-2 left-1/2 transform -translate-x-1/2 bg-gray-900/90 backdrop-blur-md text-white py-3 px-6 rounded-full shadow-lg border border-gray-700/50 z-50 hidden md:flex items-center space-x-8">
        {navItems.map(({ key, href }) => (
          <a
            key={key}
            href={`#${href}`}
            onClick={(e) => handleNavClick(e, href)}
            className={`font-paragraph font-semibold transition-all duration-300 relative group ${
              activeSection === href
                ? 'text-teal-400'
                : 'text-gray-300 hover:text-teal-400'
            }`}
          >
            {translations[currentLanguage][key]}
            <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-teal-400 transition-all duration-300 ${
              activeSection === href ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
            }`} />
          </a>
        ))}

        {/* Language Selector */}
        <div className="relative" ref={languageMenuRef}>
          <button
            onClick={toggleLanguageMenu}
            className="flex items-center space-x-2 text-gray-300 hover:text-teal-400 transition-all duration-300 p-2 rounded-lg hover:bg-gray-800/50"
            aria-label="Change language"
          >
            <Globe className="w-5 h-5" />
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
              showLanguageMenu ? 'rotate-180' : ''
            }`} />
          </button>
          
          {showLanguageMenu && (
            <div className="absolute top-12 right-0 bg-gray-800/95 backdrop-blur-md text-white rounded-lg shadow-xl border border-gray-700/50 min-w-32 overflow-hidden">
              <button
                onClick={() => changeLanguage("es")}
                className={`block w-full px-4 py-3 text-left hover:bg-teal-500/20 transition-colors duration-200 ${
                  currentLanguage === 'es' ? 'bg-teal-500/30 text-teal-400' : ''
                }`}
              >
                <span className="flex items-center space-x-2">
                  <span className="text-lg">🇪🇸</span>
                  <span>Español</span>
                </span>
              </button>
              <button
                onClick={() => changeLanguage("en")}
                className={`block w-full px-4 py-3 text-left hover:bg-teal-500/20 transition-colors duration-200 ${
                  currentLanguage === 'en' ? 'bg-teal-500/30 text-teal-400' : ''
                }`}
              >
                <span className="flex items-center space-x-2">
                  <span className="text-lg">🇺🇸</span>
                  <span>English</span>
                </span>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Navbar Mobile */}
      <nav className="fixed top-2 left-1/2 transform -translate-x-1/2 bg-gray-900/90 backdrop-blur-md text-white py-2 px-4 rounded-full shadow-lg border border-gray-700/50 z-50 md:hidden">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-teal-400">Portfolio</span>
          <button
            onClick={toggleMobileMenu}
            className="text-gray-300 hover:text-teal-400 transition-colors duration-200"
            aria-label={translations[currentLanguage].menu}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-14 left-0 right-0 bg-gray-900/95 backdrop-blur-md rounded-lg shadow-xl border border-gray-700/50 py-4">
            {navItems.map(({ key, href }) => (
              <a
                key={key}
                href={`#${href}`}
                onClick={(e) => handleNavClick(e, href)}
                className={`block px-6 py-3 font-paragraph font-semibold transition-colors duration-200 ${
                  activeSection === href
                    ? 'text-teal-400 bg-teal-500/10'
                    : 'text-gray-300 hover:text-teal-400 hover:bg-gray-800/50'
                }`}
              >
                {translations[currentLanguage][key]}
              </a>
            ))}
            
            <div className="border-t border-gray-700/50 mt-2 pt-2 px-6">
              <div className="flex items-center space-x-4">
                <Globe className="w-5 h-5 text-gray-400" />
                <div className="flex space-x-2">
                  <button
                    onClick={() => changeLanguage("es")}
                    className={`px-3 py-1 rounded-md text-sm transition-colors duration-200 ${
                      currentLanguage === 'es' 
                        ? 'bg-teal-500/30 text-teal-400' 
                        : 'text-gray-300 hover:bg-gray-800/50'
                    }`}
                  >
                    ES
                  </button>
                  <button
                    onClick={() => changeLanguage("en")}
                    className={`px-3 py-1 rounded-md text-sm transition-colors duration-200 ${
                      currentLanguage === 'en' 
                        ? 'bg-teal-500/30 text-teal-400' 
                        : 'text-gray-300 hover:bg-gray-800/50'
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;